<!-- 车牌号输入：车牌键盘 + 车库树下拉（模糊搜索、单选/多选） -->
<template>
  <div ref="wrap" class="plate-license-field" @focusin="onWrapFocusIn">
    <PlateKeyboardInput
      ref="inputField"
      :value="displayInputValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :show-search-prefix="showSearchPrefix"
      :show-panel-search="showPanelSearch"
      v-bind="plateKeyboardExtraProps"
      @input="onPlateInput"
      @focus="onFieldFocus"
      @blur="onFieldBlur"
      @panel-toggle="onKeyboardPanelToggle"
      @panel-search="$emit('panel-search', $event)"
    />
    <div
      v-show="dropdownVisible"
      ref="dropdown"
      class="plate-license-field__dropdown"
      :style="dropdownStyle"
    >
      <GarageVehicleTreePanel
        ref="garagePanel"
        :disabled="disabled"
        :enable-tree-fetch="shouldFetchGarageTree"
        :show-checkbox="multiple"
        :compact="true"
        :show-toolbar="showDropdownToolbar"
        :show-settings="showSettings"
        :external-search-text="multiple ? '' : plateSearchTextForTree"
        :garage-tree-list-url="garageTreeListUrl"
        :garage-tree-list-use-request="garageTreeListUseRequest"
        :garage-tree-children-url="garageTreeChildrenUrl"
        :garage-tree-children-use-request="garageTreeChildrenUseRequest"
        :vehicle-manage-path="vehicleManagePath"
        @vehicle-select="onGarageVehicleSelect"
        @vehicle-check-change="onGarageVehicleCheckChange"
        @open-vehicle-manage="onOpenVehicleManage"
      />
    </div>
  </div>
</template>

<script>
import PlateKeyboardInput from '@/components/PlateKeyboardInput.vue'
import GarageVehicleTreePanel from '@/components/GarageVehicleTreePanel.vue'

/** 多实例并存时，仅保留当前展开的车库下拉，避免互相干扰抢焦点 */
let activePlateLicenseDropdown = null

function normalizeMultiValue(raw) {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (typeof item === 'string') {
        const plate = String(item).trim()
        return plate ? { vehicleNo: plate, vehiclePlateColor: '' } : null
      }
      const plate = item && item.vehicleNo != null ? String(item.vehicleNo).trim() : ''
      if (!plate) return null
      return {
        vehicleNo: plate,
        vehiclePlateColor:
          item && item.vehiclePlateColor != null ? String(item.vehiclePlateColor) : ''
      }
    })
    .filter(Boolean)
}

export default {
  name: 'PlateLicenseField',
  components: { PlateKeyboardInput, GarageVehicleTreePanel },
  props: {
    value: {
      type: [String, Array],
      default: ''
    },
    placeholder: {
      type: String,
      default: '输入车牌号'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /** 为 false 时不请求车库树（如批量轨迹详情只读页） */
    enableTreeFetch: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    showSearchPrefix: {
      type: Boolean,
      default: false
    },
    showPanelSearch: {
      type: Boolean,
      default: false
    },
    showDropdownToolbar: {
      type: Boolean,
      default: true
    },
    showSettings: {
      type: Boolean,
      default: true
    },
    areaDatas: {
      type: Array,
      default: null
    },
    garageTreeListUrl: {
      type: String,
      default: ''
    },
    garageTreeListUseRequest: {
      type: Boolean,
      default: false
    },
    garageTreeChildrenUrl: {
      type: String,
      default: ''
    },
    garageTreeChildrenUseRequest: {
      type: Boolean,
      default: false
    },
    vehicleManagePath: {
      type: String,
      default: '/vehicles'
    },
    remoteSelect: {
      type: Boolean,
      default: false
    },
    fetchRemoteOptions: {
      type: Function,
      default: null
    },
    debounceMs: {
      type: Number,
      default: 300
    },
    remoteShowAllOnFocus: {
      type: Boolean,
      default: true
    },
    /**
     * 为 true 时：输入过程中仅更新本地草稿，不向父级 v-model 同步，避免列表多实例时父组件重绘导致失焦。
     * 失焦、选车、flushDraftToModel() 时再同步。
     */
    deferModelSync: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dropdownVisible: false,
      dropdownStyle: {},
      plateSearchText: '',
      /** deferModelSync 模式下聚焦时的本地草稿 */
      localDraft: '',
      fieldFocused: false,
      /** 传给车库树的搜索词（defer 模式下防抖，避免每键重绘抢焦点） */
      plateSearchTextForTree: '',
      plateSearchDebounceTimer: null,
      /** 首次展开下拉后再拉车库树，避免页面加载时每个输入框各请求一次 */
      garageTreeFetchActivated: false
    }
  },
  computed: {
    shouldFetchGarageTree() {
      return this.enableTreeFetch && this.garageTreeFetchActivated
    },
    plateKeyboardExtraProps() {
      if (this.areaDatas != null && Array.isArray(this.areaDatas)) {
        return { areaDatas: this.areaDatas }
      }
      return {}
    },
    displayInputValue() {
      if (!this.multiple) {
        if (this.deferModelSync && this.fieldFocused) {
          return this.localDraft != null ? String(this.localDraft) : ''
        }
        return this.value != null ? String(this.value) : ''
      }
      const arr = normalizeMultiValue(this.value)
      if (!arr.length) return ''
      const plates = arr.map((x) => x.vehicleNo)
      if (plates.length <= 3) return plates.join(', ')
      return `${plates.slice(0, 3).join(', ')} 等${plates.length}辆`
    }
  },
  created() {
    if (!this.multiple) {
      const str = this.value != null ? String(this.value) : ''
      this.plateSearchText = str
      this.plateSearchTextForTree = str
      this.localDraft = str
    }
  },
  watch: {
    dropdownVisible(v) {
      if (v) {
        this.$nextTick(() => {
          this.portalDropdownToBody()
          this.updateDropdownPosition()
          requestAnimationFrame(() => {
            this.updateDropdownPosition()
          })
          document.addEventListener('mousedown', this.onDocMousedown, true)
          window.addEventListener('resize', this.updateDropdownPosition)
          window.addEventListener('scroll', this.updateDropdownPosition, true)
          const panel = this.$refs.garagePanel
          if (panel && typeof panel.syncSearchExpandState === 'function') {
            panel.syncSearchExpandState()
          }
        })
      } else {
        document.removeEventListener('mousedown', this.onDocMousedown, true)
        window.removeEventListener('resize', this.updateDropdownPosition)
        window.removeEventListener('scroll', this.updateDropdownPosition, true)
        this.unportalDropdown()
        this.dropdownStyle = {}
      }
    },
    value(v) {
      if (!this.multiple) {
        const str = v != null ? String(v) : ''
        if (!this.fieldFocused) {
          this.localDraft = str
        }
        if (!this.fieldFocused || !this.deferModelSync) {
          this.plateSearchText = str
          this.syncPlateSearchTextForTree(str, true)
        }
      }
    },
    '$route'() {
      this.dismissFloatingPanels()
    }
  },
  beforeDestroy() {
    if (activePlateLicenseDropdown === this) {
      activePlateLicenseDropdown = null
    }
    if (this.plateSearchDebounceTimer) {
      clearTimeout(this.plateSearchDebounceTimer)
      this.plateSearchDebounceTimer = null
    }
    this.dismissFloatingPanels()
    document.removeEventListener('mousedown', this.onDocMousedown, true)
    window.removeEventListener('resize', this.updateDropdownPosition)
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
    this.unportalDropdown()
  },
  methods: {
    getDropdownAnchorEl() {
      const input = this.$refs.inputField
      if (input && input.$refs && input.$refs.root) return input.$refs.root
      return this.$refs.wrap
    },
    portalDropdownToBody() {
      const dropdown = this.$refs.dropdown
      if (!dropdown || typeof document === 'undefined') return
      if (dropdown.parentNode !== document.body) {
        document.body.appendChild(dropdown)
      }
    },
    unportalDropdown() {
      const dropdown = this.$refs.dropdown
      const wrap = this.$refs.wrap
      if (!dropdown || !wrap) return
      if (dropdown.parentNode === document.body) {
        wrap.appendChild(dropdown)
      }
    },
    onWrapFocusIn(e) {
      if (this.disabled) return
      const target = e && e.target
      if (this.isKeyboardUiTarget(target)) return
      const input = this.$refs.inputField
      if (input && input.show) return
      if (this.deferModelSync && this.dropdownVisible) return
      this.openDropdown()
    },
    isKeyboardUiTarget(target) {
      if (!target || !target.closest) return false
      return !!(
        target.closest('.plate-keyboard-trigger') ||
        target.closest('.plate-keyboard-panel')
      )
    },
    onOpenVehicleManage() {
      this.dismissFloatingPanels()
    },
    dismissFloatingPanels() {
      this.closeDropdown()
      this.hideKeyboardPanel()
    },
    onKeyboardPanelToggle(payload) {
      const visible = payload === true || !!(payload && payload.visible)
      if (visible) {
        this.closeDropdown()
        return
      }
      const reason = payload && typeof payload === 'object' ? payload.reason : ''
      if (reason === 'outside') return
      this.$nextTick(() => {
        this.openDropdown()
      })
    },
    hideKeyboardPanel() {
      const input = this.$refs.inputField
      if (input && input.show && typeof input.panelHide === 'function') {
        input.panelHide()
      }
    },
    activateGarageTreeFetch() {
      if (!this.enableTreeFetch || this.garageTreeFetchActivated) return
      this.garageTreeFetchActivated = true
    },
    openDropdown() {
      if (this.disabled) return
      if (activePlateLicenseDropdown && activePlateLicenseDropdown !== this) {
        activePlateLicenseDropdown.closeDropdown()
      }
      activePlateLicenseDropdown = this
      this.activateGarageTreeFetch()
      const input = this.$refs.inputField
      if (input && input.show) return
      this.hideKeyboardPanel()
      this.dropdownStyle = {
        position: 'fixed',
        left: '-9999px',
        top: '0',
        width: '320px',
        zIndex: 5000,
        visibility: 'hidden'
      }
      this.dropdownVisible = true
    },
    closeDropdown() {
      this.dropdownVisible = false
      if (activePlateLicenseDropdown === this) {
        activePlateLicenseDropdown = null
      }
    },
    updateDropdownPosition() {
      const anchor = this.getDropdownAnchorEl()
      const dropdown = this.$refs.dropdown
      if (!anchor || !dropdown || !this.dropdownVisible) return
      const r = anchor.getBoundingClientRect()
      const margin = 8
      const vw = typeof window !== 'undefined' ? window.innerWidth : 800
      const vh = typeof window !== 'undefined' ? window.innerHeight : 600
      const desiredW = Math.max(r.width, 320)
      const w = Math.min(desiredW, vw - margin * 2)
      let left = r.left
      if (left + w > vw - margin) left = Math.max(margin, vw - margin - w)
      if (left < margin) left = margin
      const gap = 4
      const maxH = Math.min(352, vh - margin * 2)
      let top = r.bottom + gap
      if (top + maxH > vh - margin) {
        top = Math.max(margin, r.top - maxH - gap)
      }
      this.dropdownStyle = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${Math.round(w)}px`,
        maxHeight: `${Math.round(maxH)}px`,
        zIndex: 5000,
        visibility: 'visible'
      }
    },
    syncPlateSearchTextForTree(text, immediate) {
      const str = text != null ? String(text) : ''
      if (this.plateSearchDebounceTimer) {
        clearTimeout(this.plateSearchDebounceTimer)
        this.plateSearchDebounceTimer = null
      }
      if (immediate || !this.deferModelSync) {
        this.plateSearchTextForTree = str
        return
      }
      this.plateSearchDebounceTimer = setTimeout(() => {
        this.plateSearchTextForTree = str
        this.plateSearchDebounceTimer = null
      }, 120)
    },
    onPlateInput(v) {
      const str = v != null ? String(v) : ''
      if (!this.multiple) {
        this.plateSearchText = str
        this.syncPlateSearchTextForTree(str, false)
        if (this.deferModelSync) {
          this.localDraft = str
          // 清除需立即同步父级：非聚焦时 displayInputValue 读 value，仅改 localDraft 会看起来清不掉
          if (str === '') {
            this.commitDraftToModel()
          }
        } else {
          this.$emit('input', str)
          this.$emit('change', str)
        }
      }
      if (!this.dropdownVisible) this.openDropdown()
      else this.$nextTick(() => this.updateDropdownPosition())
    },
    onFieldFocus() {
      this.fieldFocused = true
      if (this.deferModelSync && !this.multiple) {
        this.localDraft = this.value != null ? String(this.value) : ''
        this.plateSearchText = this.localDraft
        this.syncPlateSearchTextForTree(this.localDraft, true)
      }
    },
    commitDraftToModel() {
      if (this.multiple || !this.deferModelSync) return
      const str = this.localDraft != null ? String(this.localDraft) : ''
      this.plateSearchText = str
      this.syncPlateSearchTextForTree(str, true)
      const prev = this.value != null ? String(this.value) : ''
      if (str === prev) return
      this.$emit('input', str)
      this.$emit('change', str)
    },
    flushDraftToModel() {
      if (this.multiple || !this.deferModelSync) return
      this.commitDraftToModel()
    },
    onFieldBlur() {
      this.fieldFocused = false
      if (this.deferModelSync && !this.multiple) {
        this.commitDraftToModel()
      }
      this.$emit('blur')
    },
    onGarageVehicleSelect(payload) {
      if (!payload || this.multiple) return
      const plate = payload.value != null ? String(payload.value).trim() : ''
      if (!plate) return
      this.plateSearchText = plate
      this.localDraft = plate
      this.syncPlateSearchTextForTree(plate, true)
      this.$emit('input', plate)
      this.$emit('change', plate)
      this.$emit('vehicle-select', {
        value: plate,
        vehiclePlateColor: payload.vehiclePlateColor != null ? payload.vehiclePlateColor : ''
      })
      this.closeDropdown()
    },
    onGarageVehicleCheckChange(info) {
      if (!this.multiple || !info) return
      const cur = normalizeMultiValue(this.value)
      const map = new Map()
      cur.forEach((item) => {
        map.set(`${item.vehicleNo}__${item.vehiclePlateColor || ''}`, item)
      })
      const vehicles = Array.isArray(info.vehicles) ? info.vehicles : []
      const checked = !!info.checked
      vehicles.forEach((v) => {
        const plate = v && v.vclN != null ? String(v.vclN).trim() : ''
        if (!plate) return
        const key = `${plate}__${v.vco != null ? v.vco : ''}`
        if (checked) {
          map.set(key, { vehicleNo: plate, vehiclePlateColor: v.vco != null ? String(v.vco) : '' })
        } else {
          map.delete(key)
        }
      })
      const next = [...map.values()]
      this.$emit('input', next)
      this.$emit('change', next)
    },
    onDocMousedown(e) {
      const wrap = this.$refs.wrap
      const dropdown = this.$refs.dropdown
      const input = this.$refs.inputField
      const kbPanel = input && input.$refs ? input.$refs.panel : null
      if (wrap && e.target && wrap.contains(e.target)) return
      if (dropdown && e.target && dropdown.contains(e.target)) return
      if (kbPanel && e.target && kbPanel.contains(e.target)) return
      this.closeDropdown()
    }
  }
}
</script>

<style scoped>
.plate-license-field {
  position: relative;
  width: 100%;
}

.plate-license-field__dropdown {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 0.5rem;
  min-width: 18rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
</style>
